import React, { useState } from "react"
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  Menu,
  MenuItem,
  Backdrop
} from "@mui/material"
import { useTheme } from "@mui/system"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import AppPagination from "components/StyledComponents/AppPagination"
import TableNoRecords from "components/TableNoRecords"
import { formatDate, formatListWithTooltip, toTitleCase } from "helpers/functions"
import { useDispatch, useSelector } from "react-redux"
import { DMVActions } from "redux/DMV/slice/user"

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px"
}))

const ContestListTable = ({
  columns = [],
  rows = [],
  setPage,
  currentPage,
  totalPage,
  pageSize = 10,
  onView,
  onEdit,
  onPublish
}) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)
  const openMenu = Boolean(anchorEl)
  const dispatch = useDispatch()
  const { isLoading } = useSelector((state) => state?.app)

  const handleMenuClick = (e, row) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
    dispatch(DMVActions.setLeagueId(row?.id))
    setSelectedRow(row)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedRow(null)
  }

  const handleView = () => {
    if (selectedRow && onView) {
      onView(selectedRow)
    }
    handleMenuClose()
  }

  const handleEdit = () => {
    if (selectedRow && onEdit) {
      onEdit(selectedRow)
    }
    handleMenuClose()
  }

  const handlePublish = () => {
    if (selectedRow && onPublish) {
      onPublish(selectedRow)
    }
    handleMenuClose()
  }

  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case "running":
        return { color: theme.palette.green.light }
      case "draft":
        return { color: theme.palette.orange.main }
      case "completed":
        return { color: theme.palette.green.main }
      default:
        return { color: theme.palette.text.primary }
    }
  }

  return (
    <>
      <Paper>
        <TableContainer sx={{ maxHeight: 700 }}>
          <CustomTable stickyHeader aria-label="contest table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}>
                    <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
                      {column.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.length ? (
                rows?.map((row, index) => {
                  const globalIndex = (currentPage - 1) * pageSize + index + 1
                  return (
                    <TableRow key={row.id || index}>
                      <TableCell>
                        <Typography variant="body1Regular">
                          {String(globalIndex).padStart(3, "0")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1Regular">
                          {row?.name || row?.podName || "Pod Name"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1Regular">
                          {formatListWithTooltip(row?.restaurant_names, theme)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1Regular">
                          {formatDate(row?.start_date || row?.startDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1Regular">
                          {formatDate(row?.end_date || row?.endDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1Regular" sx={getStatusStyles(row?.status)}>
                          {toTitleCase(row?.status)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <span
                          onClick={(e) => handleMenuClick(e, row)}
                          style={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center"
                          }}>
                          <MoreHorizIcon sx={{ height: "24px", width: "24px" }} />
                        </span>
                      </TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableNoRecords
                  colSpan={columns.length}
                  label={
                    isLoading ? "Please wait we are fetching the contests..." : "No Contests Found"
                  }
                />
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
      </Paper>

      <Backdrop sx={{ color: "#fff", zIndex: 1300 }} open={openMenu} onClick={handleMenuClose}>
        <Menu
          id="contest-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              marginTop: "15px",
              backgroundColor: theme.palette.background.default,
              borderRadius: "12px",
              "& .MuiList-root": {
                backgroundColor: theme.palette.background.default,
                width: "200px",
                boxSizing: "border-box",
                padding: "10px 0"
              }
            }
          }}>
          <StyledMenuItem onClick={handleView}>
            <Typography variant="body1Regular">View</Typography>
          </StyledMenuItem>
          {selectedRow?.status?.toLowerCase() !== "published" &&
            selectedRow?.status?.toLowerCase() !== "running" &&
            selectedRow?.status?.toLowerCase() !== "completed" && (
              <StyledMenuItem onClick={handleEdit}>
                <Typography variant="body1Regular">Edit</Typography>
              </StyledMenuItem>
            )}
          {selectedRow?.status?.toLowerCase() === "draft" && selectedRow?.total_pods > 0 && (
            <StyledMenuItem onClick={handlePublish}>
              <Typography variant="body1Regular">Publish</Typography>
            </StyledMenuItem>
          )}
        </Menu>
      </Backdrop>

      {rows?.length > 0 && (
        <AppPagination onPageChange={setPage} totalPages={totalPage} currentPage={currentPage} />
      )}
    </>
  )
}

export default ContestListTable
